import { ApplicationDecisionT } from '@consumer-frontend/nc-utils';

export const accountHomeBaseUrl = import.meta.env.VITE_NC_ACCOUNT_HOME_BASE_URL || 'https://account-home.netcredit.dev';
export const accountHomeApiBaseUrl = import.meta.env.VITE_NC_ACCOUNT_HOME_API_BASE_URL
  || `${accountHomeBaseUrl}/api`;
export const loginServerBaseUrl = import.meta.env.VITE_LOGIN_SERVER_BASE_URL || 'http://localhost:4180';
export const loginServerTenantID = import.meta.env.VITE_LOGIN_SERVER_TENANT_ID || 'netcredit_account_home_dev';
// eslint-disable-next-line max-len
export const loginWebComponentUrl = import.meta.env.VITE_LOGIN_WEB_COMPONENT_URL || 'https://login.netcredit.com/wc/login-page.min.js';
export const netcreditBaseUrl = accountHomeBaseUrl.includes('my')
  ? accountHomeBaseUrl.replace('my', 'www')
  : accountHomeBaseUrl;
export type QueryParamsT = Record<string, string>;

const urlWithQueryParams = (url: string, queryParams?: QueryParamsT) => {
  if (!queryParams) {
    return url;
  }

  const params = new URLSearchParams(queryParams);

  return `${url}?${params.toString()}`;
};

const sharedApiEndpoints = {
  login: `${loginServerBaseUrl}/login`,
  mfa: `${loginServerBaseUrl}/mfa`,
  token: `${loginServerBaseUrl}/token`,
};

const applyApiEndpoints = {
  account: `${accountHomeApiBaseUrl}/v1/accounts`,
  purchaseIovation: `${accountHomeApiBaseUrl}/v1/iovation`,
  submitApplication: `${accountHomeApiBaseUrl}/v1/submit_application`,
  v2: {
    existingEmail: `${accountHomeApiBaseUrl}/v2/emails/existing_email`,
    register: `${accountHomeApiBaseUrl}/v2/accounts/register`,
    sendEmailCode: `${accountHomeApiBaseUrl}/v2/send_email_code`,
    trackEvent: `${accountHomeApiBaseUrl}/v2/track_event`,
    trackPage: `${accountHomeApiBaseUrl}/v2/track_page`,
    verifyEmailCode: `${accountHomeApiBaseUrl}/v2/verify_email`,
  },
  validateIdentity: `${accountHomeApiBaseUrl}/v1/validate_identity`,
  verifyEligibility: `${accountHomeApiBaseUrl}/v1/accounts/verify_eligibility`,
};

const dashboardApiEndpoints = {
  accountSummary: (accountId: string) => `${accountHomeApiBaseUrl}/v1/accounts/${accountId}/summary`,
  adobeDataLayer: (accountId: string) => `${accountHomeApiBaseUrl}/v1/accounts/${accountId}/adobe_data_layer`,
  billingPeriods: (accountId: string, loanNumber: string) => (
    `${accountHomeApiBaseUrl}/v1/accounts/${accountId}/loans/${loanNumber}/billing_periods`
  ),
  cashAdvanceData: () => `${accountHomeApiBaseUrl}/v1/cash_advance/data`,
  contract: (accountId: string, loanNumber: string) => (
    `${accountHomeApiBaseUrl}/v1/accounts/${accountId}/loans/${loanNumber}/contract`
  ),
  contractPDF: (accountId: string, loanNumber: string) => (
    `${accountHomeApiBaseUrl}/v1/accounts/${accountId}/loans/${loanNumber}/pdf_contract`
  ),
  drawPaymentSchedule: (queryParams?: QueryParamsT): string => (
    urlWithQueryParams(`${accountHomeApiBaseUrl}/v2/loans/draw_payment_schedule`, queryParams)
  ),
  fullPayoffAmount: (accountId: string, loanNumber: string, queryParams?: QueryParamsT) => (
    urlWithQueryParams(
      `${accountHomeApiBaseUrl}/v1/accounts/${accountId}/loans/${loanNumber}/full_payoff_amount`,
      queryParams,
    )
  ),
  loanSummary: (accountId: string, loanNumber: string) => (
    `${accountHomeApiBaseUrl}/v1/accounts/${accountId}/loans/${loanNumber}/summary`
  ),
  makePayment: (accountId: string, loanNumber: string, queryParams?: QueryParamsT) => (
    urlWithQueryParams(
      `${accountHomeApiBaseUrl}/v1/accounts/${accountId}/loans/${loanNumber}/payments`,
      queryParams,
    )
  ),
  messages: (accountId: string, queryParams?: QueryParamsT) => (
    urlWithQueryParams(`${accountHomeApiBaseUrl}/v1/accounts/${accountId}/messages`, queryParams)
  ),
  paymentAmountDue: (accountId: string, loanNumber: string) => (
    `${accountHomeApiBaseUrl}/v1/accounts/${accountId}/loans/${loanNumber}/payment_amount_due`
  ),
  paymentCalendarDates: (accountId: string, loanNumber: string, queryParams?: QueryParamsT) => (
    urlWithQueryParams(
      `${accountHomeApiBaseUrl}/v1/accounts/${accountId}/loans/${loanNumber}/payment_calendar_dates`,
      queryParams,
    )
  ),
  paymentMethods: (accountId: string) => `${accountHomeApiBaseUrl}/v1/accounts/${accountId}/payment_methods`,
  previewDeposit: (queryParams?: QueryParamsT) => (
    urlWithQueryParams(`${accountHomeApiBaseUrl}/v1/cash_advance/preview_disbursement`, queryParams)
  ),
  scheduledPayments: (accountId: string, loanNumber: string, queryParams?: QueryParamsT) => (
    urlWithQueryParams(
      `${accountHomeApiBaseUrl}/v1/accounts/${accountId}/loans/${loanNumber}/scheduled_payments`,
      queryParams,
    )
  ),
  submitCashAdvance: () => `${accountHomeApiBaseUrl}/v1/cash_advances`,
  trackEvent: (accountId: string) => `${accountHomeApiBaseUrl}/v1/accounts/${accountId}/track_event`,
  trackPage: (accountId: string) => `${accountHomeApiBaseUrl}/v1/accounts/${accountId}/track_page`,
};

export const apiEndpoints = {
  apply: applyApiEndpoints,
  dashboard: dashboardApiEndpoints,
  shared: sharedApiEndpoints,
};

const applyRoutes = {
  address: '/address',
  authenticatedHome: '/account',
  basicInfo: '/basic-information',
  borrowingNeeds: '/borrowing-needs',
  consents: '/account/consents',
  incomeDetails: '/account/income-details',
  incomeSource: '/account/income-source',
  maritalInformation: '/marital-information',
  notFound: '/not-found',
  phoneNumber: '/phone-number',
  signin: '/signin',
  ssn: '/ssn',
  unableToContinue: '/unable-to-continue',
  verifyContactInfo: '/account/verify-contact-info',
  verifyEmail: '/verify-email',
  verifyEmailCode: '/verify-email-code',
  verifyIdentity: '/verify-identity',
  verifyIncome: '/account/verify-income',
  verifyMaritalInformation: '/account/verify-marital-information',
  verifySignin: '/verify-signin',
} as const;

const dashboardUiRoutes = {
  contract: '/contract',
  makeAPayment: '/make-a-payment',
  messageDetail: '/message-detail',
  messages: '/messages',
  notFound: '/not-found',
  paymentConfirmation: '/payment-confirmation',
  paymentError: '/payment-error',
  requestCashAdvance: '/request-cash-advance',
  requestCashAdvanceConfirmation: '/request-cash-advance/confirmation',
  requestCashAdvanceReview: '/request-cash-advance/review',
} as const;

const legacyAHUiRoutes = {
  aboutUs: `${netcreditBaseUrl}/about-us`,
  accountSummary: `${accountHomeBaseUrl}/my_account/summary`,
  badCreditLoans: `${netcreditBaseUrl}/bad-credit-loans`,
  bankInfo: `${accountHomeBaseUrl}/my_info/bank`,
  blog: `${netcreditBaseUrl}/blog`,
  californiaCollectionNotice: `${netcreditBaseUrl}/states-privacy-policy#california`,
  californiaPrivacyPolicy: `${netcreditBaseUrl}/california-privacy-policy`,
  cashAdvance: (loanNumber: string) => `${accountHomeBaseUrl}/loans/${loanNumber}/draw`,
  contactUs: `${netcreditBaseUrl}/contact-us`,
  delawareConsentDisclosure: `${netcreditBaseUrl}/rates-and-terms/delaware`,
  employmentInfo: `${accountHomeBaseUrl}/my_info/employment`,
  faq: `${netcreditBaseUrl}/faq`,
  feeSaverStatus: `${accountHomeBaseUrl}/fee_saver_status`,
  financialResources: `${netcreditBaseUrl}/resources`,
  financingTypes: `${netcreditBaseUrl}/financing-types`,
  howItWorks: `${netcreditBaseUrl}/how-it-works`,
  legacyApply: `${accountHomeBaseUrl}/apply`,
  legacyContract: (loanNumber: string) => `${accountHomeBaseUrl}/loans/${loanNumber}/contract`,
  lendingPartners: `${netcreditBaseUrl}/lending-partners`,
  lineOfCredit: `${netcreditBaseUrl}/line-of-credit`,
  loanDetails: `${accountHomeBaseUrl}/my_account/loan_details`, // used in minimum payment estimator
  loanOffersDeclined: `${accountHomeBaseUrl}/loan_offers/declined`,
  loanOffersInstallment: `${accountHomeBaseUrl}/loan_offers/installment`,
  loanOffersLoc: `${accountHomeBaseUrl}/loan_offers/loc`,
  loanOffersMulti: `${accountHomeBaseUrl}/loan_offers/multi`,
  logOut: `${accountHomeBaseUrl}/logout`,
  managePaymentMethods: `${accountHomeBaseUrl}/my_account/manage_payment_methods`,
  newsMedia: `${netcreditBaseUrl}/news-media`,
  noCreditCheckOnlineLoans: `${netcreditBaseUrl}/no-credit-check-online-loans`,
  optOut: `${accountHomeBaseUrl}/opt-out`,
  oregonPrivacyPolicy: `${netcreditBaseUrl}/oregon-privacy-policy`,
  personalInfo: `${accountHomeBaseUrl}/my_info/personal`,
  personalLoans: `${netcreditBaseUrl}/personal-loans`,
  privacyPolicy: `${netcreditBaseUrl}/privacy-policy`,
  qbPrivacyNotice: `${netcreditBaseUrl}/quill-privacy-notice`,
  quickLoans: `${netcreditBaseUrl}/quick-loans`,
  ratesAndTerms: `${netcreditBaseUrl}/rates-and-terms`,
  rbtPrivacyPolicy: `${netcreditBaseUrl}/rbt-privacy-policy`,
  signin: `${netcreditBaseUrl}/signin`,
  siteMap: `${netcreditBaseUrl}/sitemap`,
  statements: (loanNumber: string) => `${accountHomeBaseUrl}/loans/${loanNumber}/statements`,
  statesPrivacyPolicy: `${netcreditBaseUrl}/states-privacy-policy`,
  termsOfUse: `${netcreditBaseUrl}/terms-of-use`,
} as const;

export const uiRoutes = {
  apply: applyRoutes,
  dashboard: dashboardUiRoutes,
  legacyAH: legacyAHUiRoutes,
};

const LOAN_OFFER_ROUTE_BY_DECISION: Record<ApplicationDecisionT, string> = {
  declined: uiRoutes.legacyAH.loanOffersDeclined,
  installment_approved: uiRoutes.legacyAH.loanOffersInstallment,
  loc_approved: uiRoutes.legacyAH.loanOffersLoc,
  multi_product_approved: uiRoutes.legacyAH.loanOffersMulti,
};

export const getLoanOfferRoute = (decision: ApplicationDecisionT): string => LOAN_OFFER_ROUTE_BY_DECISION[decision];

export const externalLinks = {
  facebook: 'https://www.facebook.com/NetCredit',
  googlePrivacyPolicy: 'https://policies.google.com/privacy',
  googleTermsOfService: 'https://policies.google.com/terms',
  mcafee: 'https://www.mcafeesecure.com/RatingVerify?ref=netcredit.com',
  x: 'https://x.com/NetCredit',
};

export const externalApi = {
  osano: 'https://tattle.api.osano.com',
  smartyAutocomplete: 'https://us-autocomplete-pro.api.smarty.com/lookup',
};
